import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { hydrateAuth, loginThunk, loginWithGoogleThunk } from '../store/auth/authSlice';
import authService from '../services/authService';
import { fastapi } from '../services/api';
import { useTranslation } from 'react-i18next';
import GoogleSignIn from '../components/GoogleSignIn';
import { ES as FlagES, GB as FlagGB } from 'country-flag-icons/react/3x2';
import { SlidingPills } from '../components/ui/SlidingPills';
import { GhostSelect, type SelectOption } from '../components/ui/GhostSelect';
import BtnTaratic from '../components/ui/BtnTaratic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

// Simple listas para selector con búsqueda
const CLINIC_CATEGORY_KEYS = [
  'general_practice',
  'dental',
  'ophthalmology',
  'dermatology_aesthetic',
  'pediatrics',
  'gynecology_obstetrics',
  'fertility_reproduction',
  'mental_health_psychiatry',
  'physiotherapy_rehabilitation',
  'orthopedics_traumatology',
  'cardiology',
  'ambulatory_surgery',
  'urgent_care',
  'veterinary',
  'other',
] as const;

// Lista completa de zonas horarias
const TIMEZONES = [
  'Africa/Abidjan', 'Africa/Accra', 'Africa/Addis_Ababa', 'Africa/Algiers', 'Africa/Asmara', 'Africa/Bamako', 'Africa/Bangui', 'Africa/Banjul', 'Africa/Bissau', 'Africa/Blantyre', 'Africa/Brazzaville', 'Africa/Bujumbura', 'Africa/Cairo', 'Africa/Casablanca', 'Africa/Ceuta', 'Africa/Conakry', 'Africa/Dakar', 'Africa/Dar_es_Salaam', 'Africa/Djibouti', 'Africa/Douala', 'Africa/El_Aaiun', 'Africa/Freetown', 'Africa/Gaborone', 'Africa/Harare', 'Africa/Johannesburg', 'Africa/Juba', 'Africa/Kampala', 'Africa/Khartoum', 'Africa/Kigali', 'Africa/Kinshasa', 'Africa/Lagos', 'Africa/Libreville', 'Africa/Lome', 'Africa/Luanda', 'Africa/Lubumbashi', 'Africa/Lusaka', 'Africa/Malabo', 'Africa/Maputo', 'Africa/Maseru', 'Africa/Mbabane', 'Africa/Mogadishu', 'Africa/Monrovia', 'Africa/Nairobi', 'Africa/Ndjamena', 'Africa/Niamey', 'Africa/Nouakchott', 'Africa/Ouagadougou', 'Africa/Porto-Novo', 'Africa/Sao_Tome', 'Africa/Tripoli', 'Africa/Tunis', 'Africa/Windhoek',
  'America/Adak', 'America/Anchorage', 'America/Anguilla', 'America/Antigua', 'America/Araguaina', 'America/Argentina/Buenos_Aires', 'America/Argentina/Catamarca', 'America/Argentina/Cordoba', 'America/Argentina/Jujuy', 'America/Argentina/La_Rioja', 'America/Argentina/Mendoza', 'America/Argentina/Rio_Gallegos', 'America/Argentina/Salta', 'America/Argentina/San_Juan', 'America/Argentina/San_Luis', 'America/Argentina/Tucuman', 'America/Argentina/Ushuaia', 'America/Aruba', 'America/Asuncion', 'America/Atikokan', 'America/Bahia', 'America/Bahia_Banderas', 'America/Barbados', 'America/Belem', 'America/Belize', 'America/Blanc-Sablon', 'America/Boa_Vista', 'America/Bogota', 'America/Boise', 'America/Cambridge_Bay', 'America/Campo_Grande', 'America/Cancun', 'America/Caracas', 'America/Cayenne', 'America/Cayman', 'America/Chicago', 'America/Chihuahua', 'America/Costa_Rica', 'America/Creston', 'America/Cuiaba', 'America/Curacao', 'America/Danmarkshavn', 'America/Dawson', 'America/Dawson_Creek', 'America/Denver', 'America/Detroit', 'America/Dominica', 'America/Edmonton', 'America/Eirunepe', 'America/El_Salvador', 'America/Fort_Nelson', 'America/Fortaleza', 'America/Glace_Bay', 'America/Godthab', 'America/Goose_Bay', 'America/Grand_Turk', 'America/Grenada', 'America/Guadeloupe', 'America/Guatemala', 'America/Guayaquil', 'America/Guyana', 'America/Halifax', 'America/Havana', 'America/Hermosillo', 'America/Indiana/Indianapolis', 'America/Indiana/Knox', 'America/Indiana/Marengo', 'America/Indiana/Petersburg', 'America/Indiana/Tell_City', 'America/Indiana/Vevay', 'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Inuvik', 'America/Iqaluit', 'America/Jamaica', 'America/Juneau', 'America/Kentucky/Louisville', 'America/Kentucky/Monticello', 'America/Kralendijk', 'America/La_Paz', 'America/Lima', 'America/Los_Angeles', 'America/Lower_Princes', 'America/Maceio', 'America/Managua', 'America/Manaus', 'America/Marigot', 'America/Martinique', 'America/Matamoros', 'America/Mazatlan', 'America/Menominee', 'America/Merida', 'America/Metlakatla', 'America/Mexico_City', 'America/Miquelon', 'America/Moncton', 'America/Monterrey', 'America/Montevideo', 'America/Montserrat', 'America/Nassau', 'America/New_York', 'America/Nipigon', 'America/Nome', 'America/Noronha', 'America/North_Dakota/Beulah', 'America/North_Dakota/Center', 'America/North_Dakota/New_Salem', 'America/Ojinaga', 'America/Panama', 'America/Pangnirtung', 'America/Paramaribo', 'America/Phoenix', 'America/Port-au-Prince', 'America/Port_of_Spain', 'America/Porto_Velho', 'America/Puerto_Rico', 'America/Punta_Arenas', 'America/Rainy_River', 'America/Rankin_Inlet', 'America/Recife', 'America/Regina', 'America/Resolute', 'America/Rio_Branco', 'America/Santarem', 'America/Santiago', 'America/Santo_Domingo', 'America/Sao_Paulo', 'America/Scoresbysund', 'America/Sitka', 'America/St_Barthelemy', 'America/St_Johns', 'America/St_Kitts', 'America/St_Lucia', 'America/St_Thomas', 'America/St_Vincent', 'America/Swift_Current', 'America/Tegucigalpa', 'America/Thule', 'America/Thunder_Bay', 'America/Tijuana', 'America/Toronto', 'America/Tortola', 'America/Vancouver', 'America/Whitehorse', 'America/Winnipeg', 'America/Yakutat', 'America/Yellowknife',
  'Antarctica/Casey', 'Antarctica/Davis', 'Antarctica/DumontDUrville', 'Antarctica/Macquarie', 'Antarctica/Mawson', 'Antarctica/McMurdo', 'Antarctica/Palmer', 'Antarctica/Rothera', 'Antarctica/Syowa', 'Antarctica/Troll', 'Antarctica/Vostok',
  'Arctic/Longyearbyen',
  'Asia/Aden', 'Asia/Almaty', 'Asia/Amman', 'Asia/Anadyr', 'Asia/Aqtau', 'Asia/Aqtobe', 'Asia/Ashgabat', 'Asia/Atyrau', 'Asia/Baghdad', 'Asia/Bahrain', 'Asia/Baku', 'Asia/Bangkok', 'Asia/Barnaul', 'Asia/Beirut', 'Asia/Bishkek', 'Asia/Brunei', 'Asia/Chita', 'Asia/Choibalsan', 'Asia/Colombo', 'Asia/Damascus', 'Asia/Dhaka', 'Asia/Dili', 'Asia/Dubai', 'Asia/Dushanbe', 'Asia/Famagusta', 'Asia/Gaza', 'Asia/Hebron', 'Asia/Ho_Chi_Minh', 'Asia/Hong_Kong', 'Asia/Hovd', 'Asia/Irkutsk', 'Asia/Jakarta', 'Asia/Jayapura', 'Asia/Jerusalem', 'Asia/Kabul', 'Asia/Kamchatka', 'Asia/Karachi', 'Asia/Kathmandu', 'Asia/Khandyga', 'Asia/Kolkata', 'Asia/Krasnoyarsk', 'Asia/Kuala_Lumpur', 'Asia/Kuching', 'Asia/Kuwait', 'Asia/Macau', 'Asia/Magadan', 'Asia/Makassar', 'Asia/Manila', 'Asia/Muscat', 'Asia/Nicosia', 'Asia/Novokuznetsk', 'Asia/Novosibirsk', 'Asia/Omsk', 'Asia/Oral', 'Asia/Phnom_Penh', 'Asia/Pontianak', 'Asia/Pyongyang', 'Asia/Qatar', 'Asia/Qostanay', 'Asia/Qyzylorda', 'Asia/Riyadh', 'Asia/Sakhalin', 'Asia/Samarkand', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Srednekolymsk', 'Asia/Taipei', 'Asia/Tashkent', 'Asia/Tbilisi', 'Asia/Tehran', 'Asia/Thimphu', 'Asia/Tokyo', 'Asia/Tomsk', 'Asia/Ulaanbaatar', 'Asia/Urumqi', 'Asia/Ust-Nera', 'Asia/Vientiane', 'Asia/Vladivostok', 'Asia/Yakutsk', 'Asia/Yangon', 'Asia/Yekaterinburg', 'Asia/Yerevan',
  'Atlantic/Azores', 'Atlantic/Bermuda', 'Atlantic/Canary', 'Atlantic/Cape_Verde', 'Atlantic/Faroe', 'Atlantic/Madeira', 'Atlantic/Reykjavik', 'Atlantic/South_Georgia', 'Atlantic/St_Helena', 'Atlantic/Stanley',
  'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Broken_Hill', 'Australia/Currie', 'Australia/Darwin', 'Australia/Eucla', 'Australia/Hobart', 'Australia/Lindeman', 'Australia/Lord_Howe', 'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney',
  'Europe/Amsterdam', 'Europe/Andorra', 'Europe/Astrakhan', 'Europe/Athens', 'Europe/Belgrade', 'Europe/Berlin', 'Europe/Bratislava', 'Europe/Brussels', 'Europe/Bucharest', 'Europe/Budapest', 'Europe/Busingen', 'Europe/Chisinau', 'Europe/Copenhagen', 'Europe/Dublin', 'Europe/Gibraltar', 'Europe/Guernsey', 'Europe/Helsinki', 'Europe/Isle_of_Man', 'Europe/Istanbul', 'Europe/Jersey', 'Europe/Kaliningrad', 'Europe/Kiev', 'Europe/Kirov', 'Europe/Lisbon', 'Europe/Ljubljana', 'Europe/London', 'Europe/Luxembourg', 'Europe/Madrid', 'Europe/Malta', 'Europe/Mariehamn', 'Europe/Minsk', 'Europe/Monaco', 'Europe/Moscow', 'Europe/Oslo', 'Europe/Paris', 'Europe/Podgorica', 'Europe/Prague', 'Europe/Riga', 'Europe/Rome', 'Europe/Samara', 'Europe/San_Marino', 'Europe/Sarajevo', 'Europe/Saratov', 'Europe/Simferopol', 'Europe/Skopje', 'Europe/Sofia', 'Europe/Stockholm', 'Europe/Tallinn', 'Europe/Tirane', 'Europe/Ulyanovsk', 'Europe/Uzhgorod', 'Europe/Vaduz', 'Europe/Vatican', 'Europe/Vienna', 'Europe/Vilnius', 'Europe/Volgograd', 'Europe/Warsaw', 'Europe/Zagreb', 'Europe/Zaporozhye', 'Europe/Zurich',
  'Indian/Antananarivo', 'Indian/Chagos', 'Indian/Christmas', 'Indian/Cocos', 'Indian/Comoro', 'Indian/Kerguelen', 'Indian/Mahe', 'Indian/Maldives', 'Indian/Mauritius', 'Indian/Mayotte', 'Indian/Reunion',
  'Pacific/Apia', 'Pacific/Auckland', 'Pacific/Bougainville', 'Pacific/Chatham', 'Pacific/Chuuk', 'Pacific/Easter', 'Pacific/Efate', 'Pacific/Enderbury', 'Pacific/Fakaofo', 'Pacific/Fiji', 'Pacific/Funafuti', 'Pacific/Galapagos', 'Pacific/Gambier', 'Pacific/Guadalcanal', 'Pacific/Guam', 'Pacific/Honolulu', 'Pacific/Kiritimati', 'Pacific/Kosrae', 'Pacific/Kwajalein', 'Pacific/Majuro', 'Pacific/Marquesas', 'Pacific/Midway', 'Pacific/Nauru', 'Pacific/Niue', 'Pacific/Norfolk', 'Pacific/Noumea', 'Pacific/Pago_Pago', 'Pacific/Palau', 'Pacific/Pitcairn', 'Pacific/Pohnpei', 'Pacific/Port_Moresby', 'Pacific/Rarotonga', 'Pacific/Saipan', 'Pacific/Tahiti', 'Pacific/Tarawa', 'Pacific/Tongatapu', 'Pacific/Wake', 'Pacific/Wallis',
  'UTC'
];

// Función para obtener la hora actual en una zona horaria
const getCurrentTimeInTimezone = (timezone: string, locale: string): string => {
  try {
    return new Date().toLocaleTimeString(locale || 'en', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return '--:--';
  }
};

// Función para normalizar texto a comercio_key válido
const normalizeToComercioKey = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 50);
};

// Función para generar sugerencias de comercio_key
const generateComercioKeySuggestions = (email: string, nombre: string): string[] => {
  const suggestions: string[] = [];

  if (nombre) {
    suggestions.push(`${normalizeToComercioKey(nombre)}_comercio`);
    suggestions.push(`${normalizeToComercioKey(nombre)}_tienda`);
  }

  if (email) {
    const emailPart = email.split('@')[0];
    suggestions.push(`${normalizeToComercioKey(emailPart)}_comercio`);
    suggestions.push(`${normalizeToComercioKey(emailPart)}_tienda`);
  }

  return suggestions.filter(s => s.length >= 3);
};

const PasswordToggle = ({ visible, onToggle }: { visible: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={visible ? 'Hide password' : 'Show password'}
    className="btn-password-toggle absolute inset-y-0 right-0 flex w-12 items-center justify-center p-0 text-gray-600 hover:text-gray-900 focus:outline-none transition-colors cursor-pointer"
  >
    <FontAwesomeIcon
      icon={visible ? faEyeSlash : faEye}
      className="text-base transition-colors"
    />
  </button>
);

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegister, setShowRegister] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [show2fa, setShow2fa] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [tempEmail, setTempEmail] = useState('');

  // Register form state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPassword2, setRegPassword2] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegPassword2, setShowRegPassword2] = useState(false);
  const [regComercioKey, setRegComercioKey] = useState('');
  const [regNombre, setRegNombre] = useState('');
  const [regTelefono, setRegTelefono] = useState('');
  const [regDireccion, setRegDireccion] = useState('');
  const [regDescripcion, setRegDescripcion] = useState('');
  const [regCategoria, setRegCategoria] = useState('');
  const [regTimezone, setRegTimezone] = useState('Europe/Madrid');
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState<string | null>(null);
  const [registrationEmailDeliveryFailed, setRegistrationEmailDeliveryFailed] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState<string | null>(null);

  // Resend Verification state
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);

  // Google Sign-In state
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showGoogleKeyPrompt, setShowGoogleKeyPrompt] = useState(false);
  const [googleComercioKey, setGoogleComercioKey] = useState('');
  const [googleTimezone, setGoogleTimezone] = useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid';
    } catch {
      return 'Europe/Madrid';
    }
  });
  const [googleSuggestions, setGoogleSuggestions] = useState<string[]>([]);
  const [lastGoogleCredential, setLastGoogleCredential] = useState<string | null>(null);
  const googleKeyCardRef = React.useRef<HTMLFormElement | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const isRegisterFormValid = React.useMemo(() => {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim());
    const passwordValid = regPassword.length >= 8;
    const password2Valid = regPassword2 === regPassword && regPassword2.length >= 8;
    const comercioKeyValid = regComercioKey.trim().length >= 3;
    return emailValid && passwordValid && password2Valid && comercioKeyValid;
  }, [regEmail, regPassword, regPassword2, regComercioKey]);

  const categoryOptions: SelectOption[] = React.useMemo(() => {
    return CLINIC_CATEGORY_KEYS.map((key) => {
      const translated = t(`clinicCategories.${key}`);
      return {
        value: translated,
        label: translated,
      };
    });
  }, [t]);

  const timezoneOptions: SelectOption[] = React.useMemo(() => {
    return TIMEZONES.map((tz) => ({
      value: tz,
      label: tz,
      subtitle: getCurrentTimeInTimezone(tz, i18n.language),
    }));
  }, [i18n.language]);

  const setLanguage = (lng: 'en' | 'es') => {
    try {
      i18n.changeLanguage(lng);
      try { localStorage.setItem('ui_language', lng); } catch { /* Best-effort cleanup. */ }
    } catch { /* Best-effort cleanup. */ }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setError(null);
    setForgotSuccess(null);
    try {
      const response = await fastapi.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSuccess(response.data.message || 'Email enviado.');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.message || err.message || 'Error al conectar');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(null);
    setError(null);
    try {
      const response = await fastapi.post('/auth/resend-verification', { email: email || regEmail });
      setResendSuccess(response.data.message || 'Correo reenviado exitosamente.');
      setRegistrationEmailDeliveryFailed(false);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(
        (typeof detail === 'string' ? detail : detail?.message) ||
        err.response?.data?.message ||
        err.message ||
        'Error al conectar con el servidor.'
      );
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoginLoading(true);
    try {
      const result: any = await dispatch(loginThunk({ email, password })).unwrap();
      if (result.requires_2fa) {
        setTempEmail(email);
        setShow2fa(true);
        setLoginLoading(false);
        return;
      }
      if (result.onboarding_required) {
        try {
          localStorage.setItem('onboarding_mode', 'register_email');
          localStorage.setItem('onboarding_done', 'false');
          localStorage.setItem('onboarding_step', '1');
        } catch { /* Best-effort cleanup. */ }
        navigate('/onboarding');
      } else {
        try { localStorage.setItem('onboarding_done', 'true'); } catch { /* Best-effort cleanup. */ }
        navigate('/');
      }
    } catch (err: any) {
      const accessDetail = err.response?.data?.detail || err.data?.detail;
      if (accessDetail?.code === 'access_not_allowed') return;
      let errStr = err.response?.data?.detail || err.response?.data?.message || err.data?.detail || err.data?.message || err.message || t('loginPage.errors.login');
      if (typeof errStr === 'string') {
        if (errStr.toLowerCase().includes('invalid credentials')) errStr = t('loginPage.errors.invalidCredentials');
        else if (errStr.includes('Email not verified')) errStr = t('loginPage.errors.emailNotVerified');
        else if (errStr.toLowerCase().includes('user not found')) errStr = t('loginPage.errors.userNotFound');
      }
      if (typeof errStr !== 'string') {
        errStr = errStr?.message || JSON.stringify(errStr);
      }
      setError(String(errStr));
      console.error('Login error:', err);
      setLoginLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoginLoading(true);
    try {
      const data = await authService.verify2FA(tempEmail, twoFactorCode);
      if (data.access_token) {
        if (data.onboarding_required) {
          try {
            localStorage.setItem('onboarding_mode', 'register_email');
            localStorage.setItem('onboarding_done', 'false');
            localStorage.setItem('onboarding_step', '1');
          } catch { /* Best-effort cleanup. */ }
        } else {
          try { localStorage.setItem('onboarding_done', 'true'); } catch { /* Best-effort cleanup. */ }
        }
        await dispatch(hydrateAuth()).unwrap();
        navigate(data.onboarding_required ? '/onboarding' : '/');
      }
    } catch (err: any) {
      if (err.response?.data?.detail?.code === 'access_not_allowed') return;
      const detail = err.response?.data?.detail;
      setError(
        String(
          (typeof detail === 'string' ? detail : detail?.message) ||
          err.response?.data?.message ||
          'Invalid 2FA code'
        )
      );
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegLoading(true);
    setRegSuccess(null);
    setRegistrationEmailDeliveryFailed(false);
    setError(null);
    try {
      const registration = await authService.register({
        email: regEmail,
        password: regPassword,
        comercio_key: regComercioKey,
        nombre: regNombre || regComercioKey,
        telefono: regTelefono || undefined,
        direccion: regDireccion || undefined,
        descripcion: regDescripcion || undefined,
        categoria: regCategoria || undefined,
        timezone_iana: regTimezone || undefined,
      });
      setEmail(regEmail.trim());
      if (registration.email_sent === false) {
        setRegistrationEmailDeliveryFailed(true);
        setRegSuccess('La tienda se creó correctamente, pero no pudimos enviar el correo de verificación. Puedes reenviarlo ahora.');
      } else {
        setRegSuccess(t('loginPage.register.success') + " Por favor, verifica tu correo antes de iniciar sesión.");
      }
      // setIsLogin(true);
      setRegPassword('');
    } catch (err: any) {
      if (err.response?.data?.detail?.code === 'access_not_allowed') return;
      let rawMsg = err.response?.data?.detail || err.response?.data?.message || err.message || t('loginPage.errors.register');
      if (typeof rawMsg === 'object') {
        if (Array.isArray(rawMsg) && rawMsg[0]?.msg) {
          rawMsg = rawMsg[0].msg;
        } else {
          rawMsg = JSON.stringify(rawMsg);
        }
      }
      setError(String(rawMsg));
      console.error('Register error:', err);
    } finally {
      setRegLoading(false);
    }
  };

  // Handle initial Google credential (button click)
  const handleGoogleCredential = async (credential: string) => {
    setError(null);
    setGoogleLoading(true);
    setLastGoogleCredential(credential);
    try {
      // Attempt normal GIS login. If user exists, this succeeds.
      const result: any = await dispatch(
        loginWithGoogleThunk({
          credential
        })
      ).unwrap();
      if (result.onboarding_required) {
        try {
          localStorage.setItem('onboarding_mode', 'register_google');
          localStorage.setItem('onboarding_done', 'false');
          localStorage.setItem('onboarding_step', '2');
        } catch { /* Best-effort cleanup. */ }
        navigate('/onboarding');
      } else {
        try { localStorage.setItem('onboarding_done', 'true'); } catch { /* Best-effort cleanup. */ }
        navigate('/');
      }
    } catch (err: any) {
      // Support both axios error and rejectWithValue payload
      const status = (err?.status ?? err?.response?.status) as number | undefined;
      const data = err?.data ?? err?.response?.data ?? {};
      const code = (data?.code || data?.detail?.code) as string | undefined;
      const suggestions = data?.suggestions as string[] | undefined;

      if (status === 409 || code === 'missing_comercio_key') {
        // New admin path: show inline card to capture comercio_key, do NOT navigate away yet
        try {
          if (Array.isArray(suggestions) && suggestions.length > 0) {
            setGoogleSuggestions(suggestions.slice(0, 5));
            setGoogleComercioKey(suggestions[0] || '');
          } else {
            setGoogleSuggestions([]);
          }
        } catch { /* Best-effort cleanup. */ }
        setError(null);
        setShowGoogleKeyPrompt(true);
        // Bring the inline card into view
        try {
          setTimeout(() => {
            googleKeyCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 0);
        } catch { /* Best-effort cleanup. */ }
      } else if (code !== 'access_not_allowed') {
        setError((data?.message as string) || 'Google Sign-In failed.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // Cancel inline Google registration and go back to choose another account
  const handleCancelGoogleRegistration = () => {
    try {
      setShowGoogleKeyPrompt(false);
      setLastGoogleCredential(null);
      setGoogleSuggestions([]);
      setGoogleComercioKey('');
      // Attempt to cancel any ongoing GIS prompt (no-op if not present)
      try {
        // @ts-ignore
        window.google?.accounts?.id?.cancel?.();
      } catch { /* Best-effort cleanup. */ }
    } catch { /* Best-effort cleanup. */ }
  };

  // Complete Google flow after collecting comercio_key
  const handleCompleteGoogleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lastGoogleCredential) {
      setError('Missing Google credential. Please click "Sign in with Google" again.');
      return;
    }
    const key = normalizeToComercioKey(googleComercioKey);
    if (!key || key.length < 3) {
      setError(t('loginPage.register.placeholders.comercioKeyRules') as string);
      return;
    }
    setGoogleLoading(true);
    setError(null);
    try {
      await dispatch(
        loginWithGoogleThunk({
          credential: lastGoogleCredential,
          comercio_key: key,
          timezone_iana: googleTimezone,
        })
      ).unwrap();

      // After creating the store, go to onboarding to optionally connect Instagram
      try {
        localStorage.setItem('onboarding_mode', 'register_google');
        localStorage.setItem('onboarding_done', 'false');
        localStorage.setItem('onboarding_step', '2'); // Jump to Instagram step
      } catch { /* Best-effort cleanup. */ }
      navigate('/onboarding');
    } catch (err: any) {
      const code = err?.data?.detail?.code || err?.response?.data?.detail?.code;
      if (code !== 'access_not_allowed') setError(err?.response?.data?.message || 'Google Sign-In failed.');
    } finally {
      setGoogleLoading(false);
    }
  };


  // Start Google OAuth fallback (server-side flow) to bypass GIS origin rules
  return (
    <div className="min-h-screen flex items-center justify-center dark-bg overflow-x-hidden p-4 sm:p-6 lg:p-8">
      <div className="relative bg-black/50 p-8 sm:p-10 rounded-3xl shadow-2xl w-full max-w-md shadow-inner-glow shadow-inner-glow-black-600">
        <div
          className="absolute top-4 right-4 flex items-center gap-2"
          aria-label={t('nav.changeLanguage')}
        >
          <SlidingPills
            options={[
              {
                id: 'es',
                label: 'ES',
                customIcon: <FlagES className="w-5 h-4 rounded-sm shadow-sm" />
              },
              {
                id: 'en',
                label: 'EN',
                customIcon: <FlagGB className="w-5 h-4 rounded-sm shadow-sm" />
              }
            ]}
            activeId={(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en'}
            onChange={(id) => setLanguage(id as 'es' | 'en')}
            className="scale-90 origin-top-right"
            size="sm"
          />
        </div>
        {!showRegister ? (
          <>
            <h1 className="pt-4 text-3xl sm:text-4xl font-extrabold mb-8 text-center text-white">{t('loginPage.loginTitle')}</h1>
            {!showGoogleKeyPrompt && (
              <>
                <GoogleSignIn onCredential={handleGoogleCredential} className="w-full flex flex-col items-center" />
                <div className="my-6 flex items-center">
                  <div className="flex-1 h-px bg-white/20" />
                  <span className="mx-3 text-xs uppercase tracking-wide text-white/60">{t('loginPage.orContinue') || 'or continue'}</span>
                  <div className="flex-1 h-px bg-white/20" />
                </div>
              </>
            )}
            {!showGoogleKeyPrompt && !show2fa && !showForgotPassword && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.email')}</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:bg-white rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base"
                    placeholder={t('loginPage.placeholders.email') as string}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.password')}</label>
                  <div className="relative mt-1">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="block w-full px-4 py-2.5 pr-12 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:bg-white rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base"
                      placeholder={t('loginPage.placeholders.password') as string}
                    />
                    <PasswordToggle visible={showPassword} onToggle={() => setShowPassword((visible) => !visible)} />
                  </div>
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setForgotEmail(email);
                        setShowForgotPassword(true);
                        setError(null);
                      }}
                      className="text-sm font-medium text-indigo-400 hover:text-indigo-300 underline focus:outline-none"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loginLoading}
                  className={`w-full flex justify-center py-3 px-4 rounded-lg shadow-md text-lg font-semibold text-white ${loginLoading ? 'opacity-70 cursor-not-allowed bg-green-500' : 'btn-taratic btn-green'}`}
                >
                  {loginLoading ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{t('loginPage.loginButton')}...</span>
                    </div>
                  ) : (
                    t('loginPage.loginButton')
                  )}
                </button>
              </form>
            )}

            {show2fa && !showForgotPassword && (
              <form onSubmit={handle2FASubmit} className="space-y-6">
                <h2 className="text-xl font-semibold text-center text-white">Verificación 2FA</h2>
                <p className="text-sm text-center text-white">Se ha enviado un código de 6 dígitos a tu correo.</p>
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-white mb-1">Código de 6 dígitos</label>
                  <input
                    type="text"
                    id="code"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    required
                    maxLength={6}
                    className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:bg-white rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base"
                    placeholder="000000"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loginLoading}
                  className={`w-full flex justify-center py-3 px-4 rounded-lg shadow-md text-lg font-semibold text-white ${loginLoading ? 'opacity-70 cursor-not-allowed bg-indigo-500' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                  {loginLoading ? 'Verificando...' : 'Verificar'}
                </button>
              </form>
            )}

            {showForgotPassword && (
              <form onSubmit={handleForgotPassword} className="space-y-6">
                <h2 className="text-xl font-semibold text-center text-white">Recuperar Contraseña</h2>
                {forgotSuccess ? (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                    <p className="text-green-800 font-medium">{forgotSuccess}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotSuccess(null);
                      }}
                      className="mt-4 btn-taratic btn-ghost-dark font-medium"
                    >
                      Volver a iniciar sesión
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-center text-white">
                      Introduce tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
                    </p>
                    <div>
                      <label htmlFor="forgotEmail" className="block text-sm font-medium text-white mb-1">Email</label>
                      <input
                        type="email"
                        id="forgotEmail"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:bg-white rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base"
                        placeholder="tu@email.com"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className={`w-full flex justify-center py-3 px-4 rounded-lg shadow-md text-lg font-semibold text-white ${forgotLoading ? 'opacity-70 cursor-not-allowed bg-indigo-500' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                    >
                      {forgotLoading ? 'Enviando...' : 'Enviar enlace'}
                    </button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(false)}
                        className="btn-taratic btn-ghost-dark text-sm underline"
                      >
                        Cancelar
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}
            {error && (
              <div className="mt-6 bg-red-50 p-4 rounded-lg border border-red-200 text-center">
                <p className="text-red-600 text-sm font-medium">
                  {error}
                </p>
                {error === t('loginPage.errors.emailNotVerified') && (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className={`mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${resendLoading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                  >
                    {resendLoading ? 'Reenviando...' : 'Reenviar correo de verificación'}
                  </button>
                )}
              </div>
            )}
            {resendSuccess && (
              <div className="mt-6 bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                <p className="text-green-800 text-sm font-medium">{resendSuccess}</p>
              </div>
            )}



            {/* comercio_key prompt for first-time Google users (inline card) */}
            {showGoogleKeyPrompt && (
              <form ref={googleKeyCardRef} onSubmit={handleCompleteGoogleRegistration} className="mt-6 space-y-4 border border-white/10 rounded-lg p-4 bg-white/5">
                <div className="flex items-start justify-between">
                  <h2 className="text-lg font-semibold text-white">
                    Step 1 of 3 — {t('loginPage.register.labels.comercioKey') || 'Choose your store key'}
                  </h2>
                  <button
                    type="button"
                    onClick={handleCancelGoogleRegistration}
                    className="btn-taratic btn-ghost-dark text-sm underline"
                    title="Go back and pick a different Google account"
                  >
                    Go back
                  </button>
                </div>
                <p className="text-xs text-white/60">
                  Pick a unique comercio_key. Use 3–50 chars: lowercase letters, numbers, underscore.
                </p>
                <input
                  type="text"
                  value={googleComercioKey}
                  onChange={(e) => setGoogleComercioKey(e.target.value)}
                  onBlur={(e) => setGoogleComercioKey(normalizeToComercioKey(e.target.value))}
                  required
                  placeholder={t('loginPage.register.placeholders.comercioKeyRules') as string}
                  className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {googleSuggestions && googleSuggestions.length > 0 && (
                  <div className="text-xs text-white/70">
                    {(t('loginPage.register.labels.suggestions') as string) || 'Suggestions'}:{' '}
                    {googleSuggestions.slice(0, 3).map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="mr-2 mb-2 inline-flex items-center px-2.5 py-1.5 border border-white/20 bg-white/10 rounded text-xs hover:bg-white/20 text-white"
                        onClick={() => setGoogleComercioKey(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1">
                    {t('loginPage.register.labels.timezoneWithNow', { time: getCurrentTimeInTimezone(googleTimezone, i18n.language) })}
                  </label>
                  <GhostSelect
                    options={timezoneOptions}
                    value={googleTimezone}
                    onChange={(val: string) => setGoogleTimezone(val)}
                    placeholder="Seleccionar zona horaria..."
                    searchable
                    searchPlaceholder="Buscar zona horaria..."
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={handleCancelGoogleRegistration}
                    className="btn-taratic btn-ghost-dark inline-flex items-center px-4 py-2 rounded-lg"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={googleLoading}
                    className="btn-taratic btn-blue inline-flex items-center px-5 py-2.5 rounded-lg"
                  >
                    {googleLoading ? (t('loginPage.register.loading') as string) : 'Create store and continue'}
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-white/50 text-center">
                  By continuing, you agree to our Terms and Privacy Policy.
                </p>
              </form>
            )}


            {/* Toggle Register */}
            <div className="mt-8 text-center text-sm text-white/70">
              {t('loginPage.noAccount')}{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); setShowRegister(true); }} className="font-medium text-indigo-400 hover:text-indigo-300 underline">
                {t('loginPage.registerHere')}
              </a>
            </div>
          </>
        ) : (
          <>

            {/* Register Panel */}
            <h1 className="pt-4 text-3xl sm:text-4xl font-extrabold mb-8 text-center text-white">{t('loginPage.register.title')}</h1>
            {error && (
              <div className="mt-6 bg-red-50 p-4 rounded-lg border border-red-200 text-center">
                <p className="text-red-600 text-sm font-medium">{error}</p>
                {(error.toLowerCase().includes("registrado") || error.toLowerCase().includes("registered")) && (
                  <button
                    type="button"
                    onClick={() => {
                      setEmail(regEmail);
                      setShowRegister(false);
                      setError(null);
                    }}
                    className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    Ir a iniciar sesión
                  </button>
                )}
              </div>
            )}
            <form onSubmit={handleRegister} className="space-y-5">
              <div>
                <label htmlFor="regEmail" className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.email')}</label>
                <input
                  type="email"
                  id="regEmail"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  required
                  className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label htmlFor="regPassword" className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.password')}</label>
                <div className="relative mt-1">
                <input
                  type={showRegPassword ? 'text' : 'password'}
                  id="regPassword"
                  value={regPassword}
                  minLength={8}
                  onChange={(e) => setRegPassword(e.target.value)}
                  required
                  className="block w-full px-4 py-2.5 pr-12 border border-gray-300 bg-white text-gray-900 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <PasswordToggle visible={showRegPassword} onToggle={() => setShowRegPassword((visible) => !visible)} />
                </div>
                <label htmlFor="regPassword2" className="block text-sm font-medium text-white/90 mt-3 mb-1">{t('loginPage.register.labels.passwordRepeat')}</label>
                <div className="relative mt-1">
                <input
                  type={showRegPassword2 ? 'text' : 'password'}
                  id="regPassword2"
                  value={regPassword2}
                  minLength={8}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRegPassword2(v);
                    const mismatch = v !== regPassword;
                    (e.target as HTMLInputElement).setCustomValidity(mismatch ? (t('loginPage.validations.passwordsDontMatch') as string) : '');
                  }}
                  required
                  className="block w-full px-4 py-2.5 pr-12 border border-gray-300 bg-white text-gray-900 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <PasswordToggle visible={showRegPassword2} onToggle={() => setShowRegPassword2((visible) => !visible)} />
                </div>
              </div>
              <div>
                <label htmlFor="regComercioKey" className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.register.labels.comercioKey')}</label>
                <input
                  type="text"
                  id="regComercioKey"
                  value={regComercioKey}
                  onChange={(e) => setRegComercioKey(e.target.value)}
                  onBlur={(e) => setRegComercioKey(normalizeToComercioKey(e.target.value))}
                  required
                  className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t('loginPage.register.placeholders.comercioKeyRules') as string}
                />
                {/* Sugerencias */}
                {(regEmail || regNombre) && (
                  <div className="mt-2 text-xs text-white/70">
                    {t('loginPage.register.labels.suggestions')}: {generateComercioKeySuggestions(regEmail, regNombre).slice(0, 3).map((s) => (
                      <button key={s} type="button" className="mr-2 mb-2 inline-flex items-center px-2.5 py-1.5 border border-white/20 bg-white/10 rounded text-xs hover:bg-white/20 text-white" onClick={() => setRegComercioKey(s)}>
                        {s}
                      </button>
                    ))}
                  </div>
                )}

              </div>

              {/* Optional commerce details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.register.labels.shopName')}</label>
                  <input className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:bg-white rounded-lg focus:outline-none focus:ring-green-500" value={regNombre} onChange={(e) => setRegNombre(e.target.value)} placeholder={t('loginPage.placeholders.optional') as string} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.register.labels.phone')}</label>
                  <input className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:bg-white rounded-lg focus:outline-none focus:ring-green-500" value={regTelefono} onChange={(e) => setRegTelefono(e.target.value)} placeholder={t('loginPage.placeholders.optional') as string} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.register.labels.address')}</label>
                  <input className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:bg-white rounded-lg focus:outline-none focus:ring-green-500" value={regDireccion} onChange={(e) => setRegDireccion(e.target.value)} placeholder={t('loginPage.placeholders.optional') as string} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.register.labels.description')}</label>
                  <textarea className="mt-1 block w-full px-4 py-2.5 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:bg-white rounded-lg focus:outline-none focus:ring-green-500" rows={2} value={regDescripcion} onChange={(e) => setRegDescripcion(e.target.value)} placeholder={t('loginPage.placeholders.optional') as string} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.register.labels.category')}</label>
                  <GhostSelect
                    options={categoryOptions}
                    value={regCategoria}
                    onChange={(val: string) => setRegCategoria(val)}
                    placeholder={t('loginPage.placeholders.searchOrType') as string || 'Seleccionar categoría...'}
                    searchable
                    searchPlaceholder="Buscar categoría..."
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-white/90 mb-1">{t('loginPage.register.labels.timezoneWithNow', { time: getCurrentTimeInTimezone(regTimezone, i18n.language) })}</label>
                  <GhostSelect
                    options={timezoneOptions}
                    value={regTimezone}
                    onChange={(val: string) => setRegTimezone(val)}
                    placeholder="Seleccionar zona horaria..."
                    searchable
                    searchPlaceholder="Buscar zona horaria..."
                  />
                </div>
              </div>
              <BtnTaratic
                type="submit"
                color={isRegisterFormValid && !regLoading ? 'blue' : 'ghost-dark'}
                disabled={!isRegisterFormValid || regLoading}
                className={`w-full flex justify-center py-3.5 px-4 rounded-xl text-lg font-semibold text-white transition-all duration-300 ${
                  isRegisterFormValid && !regLoading
                    ? 'shadow-[0_0_25px_rgba(0,102,255,0.6)] cursor-pointer ring-2 ring-blue-400/50'
                    : 'opacity-40 cursor-not-allowed'
                }`}
              >
                {regLoading ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{(t('loginPage.register.loading') as string)}...</span>
                  </div>
                ) : (
                  (t('loginPage.register.submit') as string)
                )}
              </BtnTaratic>
            </form>
            {error && (
              <div className="mt-4 bg-red-50 p-4 rounded-lg border border-red-200 text-center animate-fade-in">
                <p className="text-red-600 text-sm font-medium">{error}</p>
                {(error.toLowerCase().includes("registrado") || error.toLowerCase().includes("registered")) && (
                  <button
                    type="button"
                    onClick={() => {
                      setEmail(regEmail);
                      setShowRegister(false);
                      setError(null);
                    }}
                    className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    Ir a iniciar sesión
                  </button>
                )}
              </div>
            )}
            {regSuccess && <p className="mt-4 text-green-600 text-center text-sm font-medium bg-green-50 p-3 rounded-lg border border-green-200">{regSuccess}</p>}
            {registrationEmailDeliveryFailed && (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="mt-3 w-full text-sm font-medium text-indigo-300 hover:text-indigo-200 disabled:opacity-60"
              >
                {resendLoading ? 'Reenviando…' : 'Reenviar correo de verificación'}
              </button>
            )}
            {resendSuccess && <p className="mt-3 text-green-300 text-center text-sm">{resendSuccess}</p>}
            <div className="mt-8 text-center text-sm text-white/70">
              {t('loginPage.alreadyHaveAccount', 'Already have an account?')} <a href="#" onClick={(e) => { e.preventDefault(); setShowRegister(false); }} className="font-medium text-indigo-400 hover:text-indigo-300 underline">{t('loginPage.loginHere', 'Log in here')}</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
